import { LitElement, html, css, PropertyValues, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dashboardStyles } from './dashboard-styles';

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

function parseNumeric(
  s: string,
): { prefix: string; value: number; decimals: number; suffix: string } | null {
  const m = s.match(/^(\D*)([\d,]+(?:\.(\d+))?)(\D*)$/);
  if (!m) return null;
  const value = Number(m[2].replace(/,/g, ''));
  if (!Number.isFinite(value)) return null;
  return { prefix: m[1], value, decimals: m[3]?.length ?? 0, suffix: m[4] };
}

@customElement('kpi-card')
export class KpiCard extends LitElement {
  static styles = [
    dashboardStyles,
    css`
      :host {
        display: block;
        height: 100%;
        position: relative;
        font-family: var(--dash-font, inherit);
        color: var(--dash-text, inherit);
      }
      :host(:hover),
      :host(:focus-within) {
        z-index: 6;
      }
      .kpi { height: 100%; box-sizing: border-box; position: relative; overflow: visible; }
      .kpi-label.has-tip {
        cursor: default;
        text-decoration: none;
      }
      .kpi.has-tip:hover,
      .kpi.has-tip:focus-within {
        z-index: 6;
      }
      .tip-bubble {
        position: absolute;
        z-index: 7;
        left: 10px;
        right: 10px;
        bottom: calc(100% + 8px);
        padding: 8px 10px;
        border: 1px solid var(--dash-border);
        border-radius: 8px;
        background: var(--dash-elevated);
        color: var(--dash-text);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.28);
        font: 400 12px/1.4 var(--dash-font, inherit);
        letter-spacing: normal;
        text-transform: none;
        white-space: normal;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transform: translateY(4px);
        transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
      }
      .kpi.has-tip:hover .tip-bubble,
      .kpi.has-tip:focus-within .tip-bubble {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
        transition-delay: 0.12s;
      }
      .kpi-value { min-height: 1.15em; }
      .kpi-delta {
        font: 600 12px/1.3 var(--dash-font-mono, ui-monospace), monospace;
        font-feature-settings: 'tnum' 1;
        margin-top: 0.5rem;
      }
      .kpi-delta .note {
        color: var(--dash-muted);
        font-weight: 400;
        margin-left: 6px;
      }
      .tone-up { color: var(--green); }
      .tone-down { color: var(--red); }
      .tone-flat { color: var(--dash-muted); }
    `,
  ];

  @property() label = '';
  @property() value: string | number = '';
  @property() sub = '';
  @property() tooltip = '';
  @property() accent = '';
  @property() delta = '';
  @property() deltaTone: 'up' | 'down' | 'flat' = 'flat';
  @property({ type: Boolean, reflect: true }) compact = false;
  @state() private display: string | number = '';
  private animated = false;
  private raf = 0;

  updated(changed: PropertyValues) {
    if (changed.has('value')) this.animateValue();
    this.fitValue();
  }

  private fitValue() {
    const el = this.renderRoot.querySelector<HTMLElement>('.kpi-value');
    if (!el) return;
    const ratio =
      el.clientWidth > 0 && el.scrollWidth > el.clientWidth ? el.clientWidth / el.scrollWidth : 1;
    el.style.transform = ratio < 1 ? `scale(${ratio})` : '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  private animateValue() {
    if (this.animated) {
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
      }
      this.display = this.value;
      return;
    }
    const target = String(this.value);
    const parsed = parseNumeric(target);
    if (!parsed || prefersReducedMotion()) {
      this.display = this.value;
      if (target !== '') this.animated = true;
      return;
    }
    this.animated = true;
    const { prefix, suffix, value: end, decimals } = parsed;
    const dur = 700;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      const cur = end * (1 - Math.pow(1 - p, 3));
      this.display =
        prefix +
        cur.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }) +
        suffix;
      if (p < 1) this.raf = requestAnimationFrame(tick);
      else this.display = target;
    };
    this.raf = requestAnimationFrame(tick);
  }

  render() {
    const tipId = 'kpi-tip';
    return html`<div class="kpi ${this.tooltip ? 'has-tip' : ''}" style=${this.accent ? `--kpi-accent:${this.accent}` : ''}>
      <div class="kpi-label">${this.label}</div>
      ${this.tooltip ? html`<div id=${tipId} class="tip-bubble" role="tooltip">${this.tooltip}</div>` : nothing}
      <div class="kpi-value">${this.display}</div>
      ${this.delta
        ? html`<div class="kpi-delta tone-${this.deltaTone}">
            ${this.delta}${this.sub ? html`<span class="note">${this.sub}</span>` : ''}
          </div>`
        : this.sub
          ? html`<div class="kpi-sub">${this.sub}</div>`
          : ''}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kpi-card': KpiCard;
  }
}
